import styled, {css} from 'styled-components';

import {
  getAlphaColor,
  getColor,
  getRem,
  gts,
  ms,
  type ThemeColor,
} from '../../../../styles';
import {Text} from '../../../../uikit/Text';
import {View} from '../../../../uikit/View';
import {HORIZONTAL_LAYOUT_WIDTH} from '../../utils/constants';

const ModalTitleContainer = styled(View)`
  height: ${ms(30)};
  align-items: center;
`;

const ModalTitle = styled(Text).attrs({weight: 'bold'})`
  font-size: ${getRem(24)};

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    font-size: ${getRem(20)};
  }
`;

const Container = styled(View)<{shouldHideBookButtonOnVerticalLayout: boolean}>`
  background-color: #fff;
  gap: ${ms(32)};

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    flex-direction: column;

    ${p =>
      p.shouldHideBookButtonOnVerticalLayout &&
      css`
        padding-block-end: ${ms(20)};
      `}
  }
`;

const AsideContainer = styled(View)`
  flex-direction: column;
  gap: ${gts('gap.xl')};
`;

const AsideHeader = styled(View)`
  justify-content: space-between;
  align-items: baseline;
`;

const AsideTitle = styled(Text).attrs({variant: 'subtitle', weight: 'bold'})``;

const LegendContainer = styled(View)`
  align-items: center;
  gap: ${gts('gap.me')};

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    align-items: baseline;
    flex-direction: column;
  }
`;

const Legend = styled(View)`
  align-items: baseline;
  gap: ${gts('gap.xs')};
`;

const TimeMetaContainer = styled(View)`
  gap: ${gts('gap.sm')};
  align-items: baseline;

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const AsideBody = styled(View)`
  flex-direction: column;
  width: ${ms(380)};
  flex: 1;

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    width: 100%;
    gap: ${gts('gap.sm')};
  }
`;

const AsideCalendarContainer = styled(View)`
  margin-block-start: ${gts('gap.me')};
  flex-direction: column;
`;

const AsideBodySpinnerContainer = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-width: ${ms(100)};
`;

const AsideFooter = styled(View)`
  justify-content: space-between;
  justify-self: end;
`;

const AsideFooterBullet = styled(View)<{color: ThemeColor; stripped?: boolean}>`
  height: ${ms(8)};
  width: ${ms(8)};
  border-radius: ${gts('borderRadius.sm')};
  background-color: ${p => getColor(p.color)(p)};

  ${p =>
    p.stripped &&
    css`
      background: repeating-linear-gradient(
        130deg,
        ${getColor(p.color)(p)},
        ${getColor(p.color)(p)} 2px,
        ${getAlphaColor(p.color, 0.5)(p)} 2px,
        ${getAlphaColor(p.color, 0.5)(p)} 4px
      );
    `}
`;

const AsideFooterCell = styled(View)`
  align-items: center;
  gap: ${ms(5)};
`;

const TimeslotsContainer = styled(View)`
  flex-direction: column;
  flex: 1;
  width: ${ms(380)};

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    width: 100%;
  }
`;

const TimeslotsList = styled(View)`
  flex-flow: row wrap;
  gap: ${gts('gap.sm')};
  height: ${ms(300)};
  align-content: flex-start;

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    height: auto;
    width: 100%;
  }
`;

const EmptyStateContainer = styled(View)`
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  max-width: ${ms(210)};
`;

const BookButtonHorizontalContainer = styled(View)<{shouldHide: boolean}>`
  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    ${p =>
      p.shouldHide &&
      css`
        display: none;
      `}
  }
`;

const BookButtonVerticalContainer = styled(View)`
  display: none;
  flex-direction: column;
  flex: 1;

  /* stylelint-disable-next-line media-query-no-invalid */
  @media screen and (max-width: ${HORIZONTAL_LAYOUT_WIDTH}px) {
    display: flex;
  }
`;

const EmptyMessage = styled(Text).attrs({color: 'text3'})`
  text-align: center;
`;

const Timezone = styled(Text).attrs({color: 'text3', variant: 'label.la'})``;

export const Layout = {
  Aside: {
    Body: AsideBody,
    Bullet: AsideFooterBullet,
    CalendarContainer: AsideCalendarContainer,
    Container: AsideContainer,
    Footer: AsideFooter,
    FooterCell: AsideFooterCell,
    Header: AsideHeader,
    Legend,
    LegendContainer,
    SpinnerContainer: AsideBodySpinnerContainer,
    Title: AsideTitle,
  },
  BookButton: {
    HorizontalContainer: BookButtonHorizontalContainer,
    VerticalContainer: BookButtonVerticalContainer,
  },
  Container,
  EmptyStateContainer,
  Modal: {
    Title: ModalTitle,
    TitleContainer: ModalTitleContainer,
  },
  TimeMeta: {
    Container: TimeMetaContainer,
    Timezone,
  },
  Timeslots: {
    Container: TimeslotsContainer,
    EmptyMessage,
    List: TimeslotsList,
  },
};
